import { useState } from "react";
import { BellRing, CalendarClock, CircleCheck, Info } from "lucide-react";
import { Card } from "../ui/Card";
import { cn } from "../../lib/cn";
import { formatNumber } from "../../lib/format";
import {
  checkinRecommendationCopy,
  lastCheckinSummary,
  nextCheckinDate,
  parseCheckinReasons,
  respondToCheckin,
  type CheckinRespondResult,
  type CheckinView,
} from "../../screens/plan-utils";
import { formatShortDate } from "../progress/utils";

const BUTTON_BASE = cn(
  "flex min-h-[44px] w-full items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold",
  "select-none transition-all duration-200 ease-out active:scale-[0.98]",
  "disabled:cursor-not-allowed disabled:opacity-50",
);

export interface WeeklyCheckinCardProps {
  readonly pending: CheckinView | null;
  readonly checkins: readonly CheckinView[];
  readonly planStartedAt: number | undefined;
  /** Resolves the check-in via api.engine.respondToWeeklyCheckin (or a demo no-op). */
  readonly onRespond: (checkinId: string, accept: boolean) => Promise<unknown>;
}

type Notice = { readonly tone: "success" | "info" | "error"; readonly text: string };

function noticeFor(result: CheckinRespondResult): Notice {
  switch (result.type) {
    case "accepted":
      return { tone: "success", text: "Adjustment accepted — your daily targets were updated." };
    case "kept":
      return { tone: "success", text: "No problem — your current daily target stays the same." };
    case "already-resolved":
      return { tone: "info", text: "This check-in was already resolved. Your latest targets are shown below." };
    default:
      return { tone: "error", text: result.message };
  }
}

function PendingCheckin({ pending, onRespond }: {
  readonly pending: CheckinView;
  readonly onRespond: (checkinId: string, accept: boolean) => Promise<unknown>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [responding, setResponding] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [resolved, setResolved] = useState(false);

  const reasons = parseCheckinReasons(pending.reasonsJson);
  const buttonsDisabled = responding || resolved;

  const handleRespond = (accept: boolean) => {
    if (buttonsDisabled) return;
    setResponding(true);
    setNotice(null);
    void respondToCheckin((acceptedFlag) => onRespond(pending.id, acceptedFlag), accept).then((result) => {
      setResponding(false);
      setNotice(noticeFor(result));
      if (result.type === "accepted" || result.type === "kept" || result.type === "already-resolved") {
        setResolved(true);
      }
    });
  };

  return (
    <div className="flex flex-col gap-3" data-testid="checkin-pending">
      <p className="text-sm leading-relaxed text-text">
        Your estimated daily expenditure changed based on your recent logs.
      </p>
      <p className="text-sm font-semibold leading-relaxed text-text" data-testid="checkin-recommendation">
        {checkinRecommendationCopy(pending.previousTargetKcal, pending.newTargetKcal)}
      </p>
      {notice && (
        <p
          role={notice.tone === "error" ? "alert" : "status"}
          className={cn(
            "rounded-xl px-3 py-2 text-xs leading-relaxed",
            notice.tone === "success" && "bg-accent-light/60 text-accent-dark",
            notice.tone === "info" && "bg-muted text-text-muted",
            notice.tone === "error" && "bg-red-50 text-danger",
          )}
          data-testid="checkin-notice"
        >
          {notice.text}
        </p>
      )}
      <div className="flex flex-col gap-2">
        <button
          type="button"
          className={cn(BUTTON_BASE, "bg-accent text-white")}
          data-testid="checkin-accept-btn"
          disabled={buttonsDisabled}
          onClick={() => handleRespond(true)}
        >
          {responding ? "Saving…" : "Accept adjustment"}
        </button>
        <button
          type="button"
          className={cn(BUTTON_BASE, "border border-border bg-surface text-text")}
          data-testid="checkin-keep-btn"
          disabled={buttonsDisabled}
          onClick={() => handleRespond(false)}
        >
          Keep current target
        </button>
        <button
          type="button"
          className={cn(BUTTON_BASE, "text-accent")}
          data-testid="checkin-explain-btn"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? "Hide explanation" : "View explanation"}
        </button>
      </div>
      {expanded && (
        <div className="rounded-xl bg-muted/60 px-3 py-2" data-testid="checkin-explanation">
          {pending.explanation.trim().length > 0 && (
            <p className="text-xs leading-relaxed text-text-muted">{pending.explanation}</p>
          )}
          {reasons.length > 0 && (
            <ul className="mt-1.5 flex flex-col gap-1.5 text-xs leading-relaxed text-text-muted" data-testid="checkin-reasons">
              {reasons.map((reason) => (
                <li key={reason} className="flex items-start gap-2">
                  <span aria-hidden className="mt-1.5 inline-block h-1 w-1 shrink-0 rounded-full bg-text-muted/60" />
                  {reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function NoPendingCheckin({ checkins, planStartedAt }: {
  readonly checkins: readonly CheckinView[];
  readonly planStartedAt: number | undefined;
}) {
  const last = checkins[0] ?? null;
  const nextDate = nextCheckinDate(last?.createdAt ?? null, planStartedAt);

  return (
    <div className="flex flex-col gap-3" data-testid="checkin-none-pending">
      <div className="flex items-center gap-3 rounded-xl bg-muted/60 px-3 py-3">
        <div className="rounded-full bg-accent-light p-2 text-accent">
          <CalendarClock size={18} strokeWidth={2} />
        </div>
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">Next weekly check-in</p>
          <p className="text-sm font-semibold text-text" data-testid="checkin-next-date">
            {nextDate !== null ? formatShortDate(nextDate) : "After your first full week of logging"}
          </p>
        </div>
      </div>
      {last !== null && (
        <div className="flex items-start gap-2" data-testid="checkin-last-summary">
          <CircleCheck size={16} className="mt-0.5 shrink-0 text-accent" />
          <p className="text-xs leading-relaxed text-text-muted">
            Last check-in {formatShortDate(last.createdAt)}: {lastCheckinSummary(last)}
          </p>
        </div>
      )}
      {last === null && (
        <p className="flex items-start gap-2 text-xs leading-relaxed text-text-muted">
          <Info size={16} className="mt-0.5 shrink-0 text-text-muted" />
          Your first check-in appears after a full week of meal and weight logs.
        </p>
      )}
    </div>
  );
}

export function WeeklyCheckinCard({ pending, checkins, planStartedAt, onRespond }: WeeklyCheckinCardProps) {
  return (
    <Card className="flex flex-col gap-3 p-4" data-testid="weekly-checkin-card">
      <div>
        <h3 className="text-base font-semibold text-text">Weekly check-in</h3>
        <p className="mt-1 text-sm text-text-muted">
          Once a week your plan reviews your progress and proposes target adjustments.
        </p>
      </div>
      {pending !== null ? (
        <PendingCheckin pending={pending} onRespond={onRespond} />
      ) : (
        <NoPendingCheckin checkins={checkins} planStartedAt={planStartedAt} />
      )}
    </Card>
  );
}

/**
 * Priority-area card for a pending proposal: accent-bordered, plain-language
 * kcal change up top, and the full accept/keep flow expanded inline.
 */
export function PlanUpdateReadyCard({ pending, onRespond }: {
  readonly pending: CheckinView;
  readonly onRespond: (checkinId: string, accept: boolean) => Promise<unknown>;
}) {
  return (
    <Card
      className="plan-rise-in flex flex-col gap-3 border-accent/40 bg-accent-light/40 p-4"
      data-testid="weekly-checkin-card"
    >
      <div className="flex items-center gap-2">
        <div className="rounded-full bg-surface p-2 text-accent">
          <BellRing size={18} strokeWidth={2} />
        </div>
        <div>
          <h3 className="text-base font-semibold text-text">Plan update ready</h3>
          <p className="text-sm font-semibold text-accent-dark" data-testid="checkin-change-summary">
            {formatNumber(pending.previousTargetKcal)} → {formatNumber(pending.newTargetKcal)} kcal/day
          </p>
        </div>
      </div>
      <PendingCheckin pending={pending} onRespond={onRespond} />
    </Card>
  );
}
